/**
 * The dither/glitter preset, lifted from the editor at
 * ~/Desktop/dither-glitter-editor.html with its saved settings.
 *
 * The editor stores everything as slider positions and divides at read time;
 * those divisions are applied here so the numbers below are the real ones. The
 * only values that differ from the saved preset are the four colours, which
 * were a generic lavender — see PALETTE.
 */

export const PRESET = {
  /** Cell edge at rest, in canvas px. */
  cellSize: 2,
  gap: 0,
  /** Luminance below this is a filled cell; above highThresh is a stroked one. */
  darkThresh: 0.18,
  highThresh: 0.65,
  midProb: 1,
  minRect: 0.05,
  maxRect: 0.8,
  strokeW: 3.2,
  glitterOn: true,
  glitterSpeed: 2.6,
  glitterDensity: 0.08,
  glitterBright: 0.8,
  shimmer: 1,
  seed: 148,
  contrast: 0.9,
  brightness: -18,
  /**
   * Share of filled cells that become a rotating plus during the hold.
   *
   * 0.62, up from the editor's 0.4. At the expanded step there are only about
   * eighty filled cells left, so 40% of them was roughly thirty plus signs
   * spread over the whole panel — the dissolved state read as a near-blank
   * card rather than a body coming apart into readings.
   */
  chaos: 0.62,
  /**
   * Ordered, via a Bayer 4x4 matrix — the editor's saved choice, and the one
   * that gives the texture its cross-hatch. A flat 0.5 threshold instead makes
   * the figure resolve in hard blocks, which is what a first pass here did and
   * why it looked coarse.
   */
  ditherPattern: 'ordered' as const,

  /* The breathe cycle, in seconds. Twelve all told. */
  phExpand: 1,
  phFade: 0.5,
  phPlus: 4,
  /* 1.4, up from the editor's 0.8. The readouts leave the way they arrive —
     an 800ms scramble, staggered — and that does not fit inside 0.8s once the
     stagger is counted. Costs half a second on a twelve second loop. */
  phPlusFade: 1.4,
  phContract: 1.7,
  phRest: 4,

  /* How far the grid opens as it expands. The gap does most of the work — the
     step goes from 2 to 25, so the figure resolves from a dense dither into a
     scatter of large cells. The zoom is only 2% on top of that. */
  breatheSize: 3,
  breatheGap: 20,
  breatheZoom: 0.02,
} as const;

/**
 * The page's blues, in place of the preset's lavender. Pulsomics runs on
 * #1539F5 with #8CA0FF as its soft tier — the same two the Pulse-Omics ring
 * uses — so the figure is built from those rather than from a palette that
 * belongs to nothing else on the page.
 */
export const PALETTE = {
  /**
   * The page's blue at about 6% over white. A tint rather than a panel: the
   * section itself is white, and the frame should read as the same paper
   * faintly coloured, not as a card dropped onto it.
   */
  bg: '#F1F3FE',
  /** Filled cells: the body's mass. Near-black, but blue rather than neutral. */
  dark: '#101A4D',
  /** Stroked cells: the lit edge. The page's blue outright. */
  highlight: '#1539F5',
  mid: '#3A4BA8',
  glitter: '#1539F5',
} as const;

export const CYCLE_S =
  PRESET.phExpand +
  PRESET.phFade +
  PRESET.phPlus +
  PRESET.phPlusFade +
  PRESET.phContract +
  PRESET.phRest;
